/**
 * Audit Log Store
 *
 * Tracks skill generation operations for debugging and analytics
 */

import { create } from 'zustand';
import { api } from '@/lib/api';
import { getActiveUserId } from '@/stores/userStore';

export interface AuditLogEntry {
  id: string;
  artifactId: string;
  eventType: 'generation_started' | 'generation_completed' | 'generation_failed' | 'artifact_overwritten' | 'manual_edit';
  stage: 'plan' | 'lumascript' | 'bpmn';
  startedAt: number;
  completedAt?: number;
  durationMs?: number;
  modelProvider?: string;
  modelName?: string;
  jobId?: string;
  status: 'in_progress' | 'completed' | 'failed' | 'timeout';
  errorMessage?: string;
  artifactSize?: number;
  wasOverwrite?: boolean;
  previousArtifactHash?: string;
  userId: string;
  createdAt: number;
}

export interface AuditState {
  // Audit logs by artifact ID
  logs: Map<string, AuditLogEntry[]>;

  // Current generation tracking
  activeGenerations: Map<string, {
    stage: 'plan' | 'lumascript' | 'bpmn';
    startedAt: number;
    auditId?: string;
  }>;

  // Actions
  fetchLogs: (artifactId: string) => Promise<void>;
  startGeneration: (artifactId: string, stage: 'plan' | 'lumascript' | 'bpmn', modelProvider?: string, modelName?: string) => Promise<string>;
  completeGeneration: (auditId: string, artifactSize?: number) => Promise<void>;
  failGeneration: (auditId: string, errorMessage: string) => Promise<void>;
  getActiveGeneration: (artifactId: string) => { stage: 'plan' | 'lumascript' | 'bpmn'; startedAt: number; auditId?: string } | null;
  clearLogs: (artifactId: string) => void;
}

export const useAuditStore = create<AuditState>((set, get) => ({
  logs: new Map(),
  activeGenerations: new Map(),

  fetchLogs: async (artifactId: string) => {
    try {
      const response = await api.get(`/audit/${artifactId}`);
      // Transform snake_case API response to camelCase
      const logs: AuditLogEntry[] = response.data.map((log: any) => ({
        id: log.id,
        artifactId: log.artifact_id,
        eventType: log.event_type,
        stage: log.stage,
        startedAt: log.started_at,
        completedAt: log.completed_at,
        durationMs: log.duration_ms,
        modelProvider: log.model_provider,
        modelName: log.model_name,
        jobId: log.job_id,
        status: log.status,
        errorMessage: log.error_message,
        artifactSize: log.artifact_size,
        wasOverwrite: log.was_overwrite === 1,
        previousArtifactHash: log.previous_artifact_hash,
        userId: log.user_id,
        createdAt: log.created_at
      }));

      set((state) => {
        const newLogs = new Map(state.logs);
        newLogs.set(artifactId, logs);
        return { logs: newLogs };
      });
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    }
  },

  startGeneration: async (artifactId: string, stage: 'plan' | 'lumascript' | 'bpmn', modelProvider?: string, modelName?: string) => {
    const startedAt = Date.now();

    try {
      const response = await api.post('/audit', {
        artifactId,
        eventType: 'generation_started',
        stage,
        startedAt,
        modelProvider,
        modelName,
        status: 'in_progress',
        userId: getActiveUserId() || 'default-user'
      });

      const auditId = response.data.id;

      // Track active generation
      set((state) => {
        const newActiveGenerations = new Map(state.activeGenerations);
        newActiveGenerations.set(artifactId, { stage, startedAt, auditId });
        return { activeGenerations: newActiveGenerations };
      });

      return auditId;
    } catch (error) {
      console.error('Failed to start audit log:', error);

      // Still track locally even if API fails
      set((state) => {
        const newActiveGenerations = new Map(state.activeGenerations);
        newActiveGenerations.set(artifactId, { stage, startedAt });
        return { activeGenerations: newActiveGenerations };
      });

      return '';
    }
  },

  completeGeneration: async (auditId: string, artifactSize?: number) => {
    if (!auditId) return;

    const completedAt = Date.now();

    try {
      await api.patch(`/audit/${auditId}`, {
        completedAt,
        status: 'completed',
        artifactSize
      });

      // Remove from active generations
      set((state) => {
        const newActiveGenerations = new Map(state.activeGenerations);
        // Find and remove the entry with this audit ID
        for (const [artifactId, gen] of newActiveGenerations.entries()) {
          if (gen.auditId === auditId) {
            newActiveGenerations.delete(artifactId);
            break;
          }
        }
        return { activeGenerations: newActiveGenerations };
      });
    } catch (error) {
      console.error('Failed to complete audit log:', error);
    }
  },

  failGeneration: async (auditId: string, errorMessage: string) => {
    if (!auditId) return;

    const completedAt = Date.now();

    try {
      await api.patch(`/audit/${auditId}`, {
        completedAt,
        status: 'failed',
        errorMessage
      });

      // Remove from active generations
      set((state) => {
        const newActiveGenerations = new Map(state.activeGenerations);
        for (const [artifactId, gen] of newActiveGenerations.entries()) {
          if (gen.auditId === auditId) {
            newActiveGenerations.delete(artifactId);
            break;
          }
        }
        return { activeGenerations: newActiveGenerations };
      });
    } catch (error) {
      console.error('Failed to fail audit log:', error);
    }
  },

  getActiveGeneration: (artifactId: string) => {
    return get().activeGenerations.get(artifactId) || null;
  },

  clearLogs: (artifactId: string) => {
    set((state) => {
      const newLogs = new Map(state.logs);
      newLogs.delete(artifactId);
      return { logs: newLogs };
    });
  }
}));
